import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import OperatorLogin from '../operations/OperatorLogin';
import './KeyAccountCheckStation.css';

/**
 * Key Account Check Station — the floor kiosk that verifies a Barnes & Noble
 * build before pickup. Every carton label, price sticker and inner pack is
 * checked against the PO lines by barcode scan (keyboard-wedge scanner) or by
 * photo read with AI, then the manual items are ticked and the run is signed
 * off. All endpoints under /api/key-account-check/station/ are public for the
 * tablet; the operator badge scan identifies who did each check.
 */

const KINDS = {
  carton: { scanKind: 'carton', photoKind: 'carton_label', label: 'Carton label', hint: 'GTIN-14 or the item UPC, qty and RETAIL price' },
  sticker: { scanKind: 'sticker', photoKind: 'price_sticker', label: 'Price sticker', hint: 'UPC-A / EAN-13 and the RETAIL price' },
  inner: { scanKind: 'inner', photoKind: 'inner_pack', label: 'Inner pack (IOQ)', hint: 'UPC-A / EAN-13 with IOQ or PACK count' },
};

function money(n) {
  const v = Number(n);
  return Number.isFinite(v) ? `$${v.toFixed(2)}` : '';
}

function countFor(counts, lineKey, types) {
  const c = counts && counts[lineKey] ? counts[lineKey] : {};
  let pass = 0; let fail = 0;
  for (const t of types) { if (c[t]) { pass += c[t].pass || 0; fail += c[t].fail || 0; } }
  return { pass, fail };
}

function Verdict({ v }) {
  if (!v) return null;
  return (
    <div className={`kas-verdict ${v.pass ? 'kas-pass' : 'kas-fail'}`}>
      <div className="kas-verdict-word">{v.pass ? 'PASS' : 'FAIL'}</div>
      {v.reasons && v.reasons.length > 0 && (
        <ul className="kas-reasons">{v.reasons.map((r, i) => <li key={i}>{r}</li>)}</ul>
      )}
      {v.reading && (
        <div className="kas-reading">
          <div className="kas-reading-title">What the photo shows</div>
          <div className="kas-reading-grid">
            <span>Title</span><span>{v.reading.title || 'not read'}</span>
            <span>Quantity</span><span>{v.reading.quantity ?? 'not read'}</span>
            <span>Barcode</span><span>{v.reading.barcodeDigits || 'not read'}</span>
            <span>Price</span><span>{v.reading.retailPrice ?? 'not read'}</span>
            {v.reading.ioqText ? <><span>IOQ text</span><span>{v.reading.ioqText}</span></> : null}
            <span>Confidence</span><span>{v.reading.confidence || ''}</span>
          </div>
        </div>
      )}
    </div>
  );
}

function LineCard({ run, line, operator, counts, onResult }) {
  const [mode, setMode] = useState('carton');
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState('');
  const [verdict, setVerdict] = useState(null);
  const [error, setError] = useState('');
  const inputRef = useRef(null);
  const fileRef = useRef(null);
  const lineKey = `${line.poNumber}|${line.vendorItem}`;
  const kind = KINDS[mode];

  useEffect(() => {
    if (inputRef.current) inputRef.current.focus();
  }, [mode]);

  const submitScan = async (e) => {
    e.preventDefault();
    const scanned = code.trim();
    setCode('');
    if (!scanned) return;
    setBusy('Checking');
    setError('');
    try {
      const res = await axios.post('/api/key-account-check/station/scan', {
        runId: run.id, poNumber: line.poNumber, vendorItem: line.vendorItem, kind: kind.scanKind, code: scanned, operator,
      });
      setVerdict(res.data);
      onResult(res.data);
    } catch (err) {
      setError(err.response?.data?.error || err.message);
    } finally {
      setBusy('');
      if (inputRef.current) inputRef.current.focus();
    }
  };

  const onPhoto = async (e) => {
    const file = e.target.files && e.target.files[0];
    e.target.value = '';
    if (!file) return;
    setBusy('Reading the photo');
    setError('');
    try {
      const form = new FormData();
      form.append('image', file);
      form.append('runId', run.id);
      form.append('poNumber', line.poNumber);
      form.append('vendorItem', line.vendorItem);
      form.append('kind', kind.photoKind);
      form.append('operator', JSON.stringify(operator || {}));
      const res = await axios.post('/api/key-account-check/station/photo', form, { headers: { 'Content-Type': 'multipart/form-data' } });
      setVerdict(res.data);
      onResult(res.data);
    } catch (err) {
      setError(err.response?.data?.error || err.message);
    } finally {
      setBusy('');
    }
  };

  const cartonC = countFor(counts, lineKey, ['carton_scan', 'carton_photo']);
  const stickerC = countFor(counts, lineKey, ['sticker_scan', 'sticker_photo']);
  const innerC = countFor(counts, lineKey, ['inner_scan', 'inner_pack_photo']);

  return (
    <div className="kas-line">
      <div className="kas-line-head">
        <div>
          <div className="kas-line-item">{line.vendorItem}</div>
          <div className="kas-line-title">{line.title}</div>
        </div>
        <div className="kas-expect">
          <div><span>EAN</span><strong>{line.ean13}</strong></div>
          <div><span>Case code</span><strong>{line.gtin14}</strong></div>
          <div><span>Casepack</span><strong>{line.casepack}</strong></div>
          <div><span>IOQ</span><strong>{line.ioq}{line.needsInnerPacks ? ` (${line.innerPacksPerCarton} packs of ${line.ioq} per carton)` : ''}</strong></div>
          <div><span>Retail</span><strong>{money(line.retail)}</strong></div>
          <div><span>Cartons</span><strong>{line.cartons}</strong></div>
        </div>
      </div>

      <div className="kas-progress">
        <div className={cartonC.pass > 0 && cartonC.fail === 0 ? 'kas-ok' : cartonC.fail > 0 ? 'kas-bad' : ''}>Carton {cartonC.pass} pass / {cartonC.fail} fail</div>
        <div className={stickerC.pass > 0 && stickerC.fail === 0 ? 'kas-ok' : stickerC.fail > 0 ? 'kas-bad' : ''}>Sticker {stickerC.pass} pass / {stickerC.fail} fail</div>
        {line.needsInnerPacks && <div className={innerC.pass > 0 && innerC.fail === 0 ? 'kas-ok' : innerC.fail > 0 ? 'kas-bad' : ''}>Inner pack {innerC.pass} pass / {innerC.fail} fail</div>}
      </div>

      <div className="kas-modes">
        {Object.entries(KINDS).filter(([k]) => k !== 'inner' || line.needsInnerPacks).map(([k, v]) => (
          <button key={k} type="button" className={`kas-mode ${mode === k ? 'kas-mode-on' : ''}`} onClick={() => { setMode(k); setVerdict(null); }}>
            {v.label}
          </button>
        ))}
      </div>

      <div className="kas-actions">
        <form onSubmit={submitScan} className="kas-scan-form">
          <input
            ref={inputRef}
            className="kas-scan-input"
            value={code}
            onChange={(e) => setCode(e.target.value)}
            placeholder={`Scan the ${kind.label.toLowerCase()} barcode`}
            autoComplete="off"
            inputMode="numeric"
          />
          <button type="submit" className="kas-btn kas-btn-primary" disabled={!!busy || !operator}>Check scan</button>
        </form>
        <div className="kas-photo">
          <input ref={fileRef} type="file" accept="image/*" capture="environment" onChange={onPhoto} hidden />
          <button type="button" className="kas-btn" disabled={!!busy || !operator} onClick={() => fileRef.current && fileRef.current.click()}>
            Photo check ({kind.label.toLowerCase()})
          </button>
        </div>
      </div>
      <div className="kas-hint">{kind.hint}. {!operator && 'Scan your badge first.'}</div>
      {busy && <div className="kas-hint">{busy}&hellip;</div>}
      {error && <div className="kas-error">{error}</div>}
      <Verdict v={verdict} />
    </div>
  );
}

function cartonPill(row) {
  if (row.status === 'error') return <span className="kas-pill kas-pill-fail">error</span>;
  if (row.scan_result === 'fail' || row.photo_result === 'fail') return <span className="kas-pill kas-pill-fail">FAIL</span>;
  if (row.status === 'pending') return <span className="kas-pill kas-pill-wait">label read pending</span>;
  return <span className="kas-pill kas-pill-pass">PASS</span>;
}

function timeOf(iso) {
  try { return new Date(iso).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' }); } catch { return ''; }
}

/**
 * Carton feed — every frame the sealer camera posted for this run, newest
 * first, with per-pallet counts and the failing cartons to pull. Polls every
 * 3 seconds while the run is open on this page.
 */
function CartonFeed({ runId, operator }) {
  const [feed, setFeed] = useState(null);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState('');

  const load = useCallback(async () => {
    try {
      const res = await axios.get(`/api/key-account-check/station/runs/${runId}/cartons?limit=20`);
      setFeed(res.data);
      setError('');
    } catch (err) {
      setError(err.response?.data?.error || err.message);
    }
  }, [runId]);

  useEffect(() => {
    load();
    const t = setInterval(load, 3000);
    return () => clearInterval(t);
  }, [load]);

  const newPallet = async () => {
    setBusy('pallet');
    try {
      await axios.post(`/api/key-account-check/station/runs/${runId}/pallet`, { operator });
      await load();
    } catch (err) {
      setError(err.response?.data?.error || err.message);
    } finally {
      setBusy('');
    }
  };

  const pulled = async (id) => {
    setBusy(id);
    try {
      await axios.post(`/api/key-account-check/station/cartons/${id}/pulled`, { operator });
      await load();
    } catch (err) {
      setError(err.response?.data?.error || err.message);
    } finally {
      setBusy('');
    }
  };

  if (!feed) return <div className="kas-hint">{error || 'Loading the carton feed'}&hellip;</div>;

  return (
    <div>
      <div className="kas-feed-head">
        <div className="kas-feed-stats">
          <span>Cartons <strong>{feed.total}</strong></span>
          <span>Pending reads <strong>{feed.pending}</strong></span>
          <span>To pull <strong>{feed.failing.length}</strong></span>
          <span>Current pallet <strong>{feed.palletNo}</strong></span>
        </div>
        <button type="button" className="kas-btn kas-btn-small kas-btn-primary" disabled={!!busy || !operator} onClick={newPallet}>New pallet</button>
      </div>
      {error && <div className="kas-error">{error}</div>}

      <div className="kas-feed-pallets">
        {feed.byPallet.map((p) => (
          <div key={String(p.pallet)} className={`kas-pallet ${p.pallet === feed.palletNo ? 'kas-pallet-on' : ''} ${p.failed - p.pulled > 0 ? 'kas-pallet-bad' : ''}`}>
            <div className="kas-pallet-no">Pallet {p.pallet == null ? '?' : p.pallet}</div>
            <div>{p.cartons} cartons{p.pending ? `, ${p.pending} pending` : ''}</div>
            <div>{p.failed - p.pulled > 0 ? `${p.failed - p.pulled} to pull` : p.failed > 0 ? `${p.pulled} pulled` : 'all good'}</div>
          </div>
        ))}
        {feed.byPallet.length === 0 && <div className="kas-hint">No cartons yet. Start the sealer camera service for run {runId.slice(0, 8)}.</div>}
      </div>

      {feed.failing.length > 0 && (
        <ul className="kas-failing">
          {feed.failing.map((c) => (
            <li key={c.id}>
              <span className="kas-failing-where">Pallet {c.pallet_no ?? '?'} · #{c.carton_seq ?? '?'}</span>
              <span className="kas-failing-why">{c.vendor_item || 'unknown line'}: {(c.reasons || []).join('; ') || 'failed'}</span>
              <button type="button" className="kas-btn kas-btn-small" disabled={!!busy || !operator} onClick={() => pulled(c.id)}>Pulled</button>
            </li>
          ))}
        </ul>
      )}

      <table className="kas-feed-table">
        <thead>
          <tr><th>Time</th><th>Pallet</th><th>#</th><th>Line</th><th>Barcode</th><th>Label read</th><th>Result</th></tr>
        </thead>
        <tbody>
          {feed.recent.map((c) => (
            <tr key={c.id} className={c.scan_result === 'fail' || c.photo_result === 'fail' || c.status === 'error' ? 'kas-row-fail' : c.status === 'pending' ? 'kas-row-pending' : ''}>
              <td>{timeOf(c.created_at)}</td>
              <td>{c.pallet_no ?? ''}</td>
              <td>{c.carton_seq ?? ''}</td>
              <td>{c.vendor_item || ''}</td>
              <td>{c.scan_result === 'pass' ? 'ok' : c.scan_result === 'fail' ? 'wrong' : ''}</td>
              <td>{c.photo_result === 'pass' ? 'ok' : c.photo_result === 'fail' ? 'wrong' : c.status === 'pending' ? 'reading' : c.status === 'error' ? 'error' : ''}</td>
              <td>{cartonPill(c)}{c.pulled_at ? ' (pulled)' : ''}</td>
            </tr>
          ))}
          {feed.recent.length === 0 && <tr><td colSpan={7} className="kas-hint">Nothing posted yet.</td></tr>}
        </tbody>
      </table>
    </div>
  );
}

export default function KeyAccountCheckStation() {
  const [operator, setOperator] = useState(null);
  const [runs, setRuns] = useState([]);
  const [runId, setRunId] = useState('');
  const [run, setRun] = useState(null);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState('');
  const [signoffMsg, setSignoffMsg] = useState('');

  const loadRuns = useCallback(async () => {
    try {
      const res = await axios.get('/api/key-account-check/station/runs');
      setRuns(res.data.runs || []);
    } catch (err) {
      setError(err.response?.data?.error || err.message);
    }
  }, []);

  const loadRun = useCallback(async (id) => {
    if (!id) { setRun(null); return; }
    try {
      const res = await axios.get(`/api/key-account-check/station/runs/${id}`);
      setRun(res.data.run);
    } catch (err) {
      setError(err.response?.data?.error || err.message);
    }
  }, []);

  useEffect(() => { loadRuns(); }, [loadRuns]);
  useEffect(() => { loadRun(runId); }, [runId, loadRun]);

  const onResult = useCallback((data) => {
    setRun((r) => (r ? { ...r, signoff: data.signoff || r.signoff } : r));
    loadRun(runId);
  }, [runId, loadRun]);

  const setManual = async (item, result) => {
    setBusy(item);
    setError('');
    try {
      await axios.post('/api/key-account-check/station/manual', { runId, item, result, operator });
      await loadRun(runId);
    } catch (err) {
      setError(err.response?.data?.error || err.message);
    } finally {
      setBusy('');
    }
  };

  const signoff = async () => {
    setBusy('signoff');
    setError('');
    setSignoffMsg('');
    try {
      const res = await axios.post('/api/key-account-check/station/signoff', { runId, operator });
      setSignoffMsg(`Signed off by ${res.data.signedOffBy || operator?.display_name || 'operator'}. This build is cleared for pickup.`);
      await loadRun(runId);
      await loadRuns();
    } catch (err) {
      if (err.response?.status === 409) {
        setSignoffMsg('Not ready: ' + (err.response.data.missing || []).map((m) => (m.vendorItem ? `${m.vendorItem} ${m.what}` : m.what)).join('; '));
      } else {
        setError(err.response?.data?.error || err.message);
      }
    } finally {
      setBusy('');
    }
  };

  const lines = useMemo(() => (run ? run.pos.flatMap((po) => po.lines) : []), [run]);

  return (
    <div className="kas-app">
      <header className="kas-header">
        <div>
          <h1>Key Account Check Station</h1>
          <p className="kas-sub">Barnes &amp; Noble &middot; check every carton label, sticker and inner pack against the PO before pickup</p>
        </div>
        <OperatorLogin onOperatorChange={setOperator} department="Key Account Check" />
      </header>

      <section className="kas-card">
        <div className="kas-field-label">1. Pick the PO build</div>
        <div className="kas-runs">
          {runs.length === 0 && <div className="kas-hint">No open PO runs. Upload and check a PO in Operations &rsaquo; Key Account PO first.</div>}
          {runs.map((r) => (
            <button key={r.id} type="button" className={`kas-run ${runId === r.id ? 'kas-run-on' : ''}`} onClick={() => { setRunId(r.id); setSignoffMsg(''); }}>
              <div className="kas-run-po">PO {r.poNumbers.join(', ')}</div>
              <div className="kas-run-meta">{r.pos.map((p) => `${p.dc?.city || ''}${p.salesOrder ? ` · SO ${p.salesOrder.tranid}` : ''}`).join(' | ')}</div>
              <div className="kas-run-meta">{r.status === 'passed' ? 'Signed off' : r.signoff.ready ? 'Ready to sign off' : `${r.signoff.missing.length} checks left`}</div>
            </button>
          ))}
        </div>
        {error && <div className="kas-error">{error}</div>}
      </section>

      {run && (
        <>
          <section className="kas-card">
            <div className="kas-field-label">2. Carton feed (sealer camera)</div>
            <div className="kas-samples-note">Every carton the sealer camera saw for this build. Pull anything listed in red before the pallet is wrapped.</div>
            <CartonFeed runId={run.id} operator={operator} />
          </section>

          <section className="kas-card">
            <div className="kas-field-label">3. Samples: check each line by hand</div>
            <div className="kas-samples-note">Sample checks with the hand scanner or the tablet camera: first carton, first sticker and first inner pack of each line, and again after a new label roll.</div>
            {run.pos.map((po) => (
              <div key={po.poNumber} className="kas-po">
                <div className="kas-po-head">
                  <strong>PO {po.poNumber}</strong>
                  <span>{po.dc?.city}, {po.dc?.state}</span>
                  {po.salesOrder && <span>SO {po.salesOrder.tranid}</span>}
                  <span>Arrival {po.expectedArrival || ''} &middot; Cancel {po.cancelDate || ''}</span>
                </div>
                {po.lines.map((line) => (
                  <LineCard key={`${po.poNumber}-${line.vendorItem}`} run={run} line={line} operator={operator} counts={run.counts} onResult={onResult} />
                ))}
              </div>
            ))}
          </section>

          <section className="kas-card">
            <div className="kas-field-label">4. Before pickup</div>
            <ul className="kas-manual">
              {run.manualItems.map((m) => {
                const state = run.manual && run.manual[m.key];
                return (
                  <li key={m.key} className={state ? (state.result === 'pass' ? 'kas-ok' : 'kas-bad') : ''}>
                    <span className="kas-manual-text">{m.label}</span>
                    <span className="kas-manual-state">{state ? `${state.result === 'pass' ? 'Done' : 'Failed'} by ${state.operator || 'operator'}` : 'Not checked'}</span>
                    <span className="kas-manual-btns">
                      <button type="button" className="kas-btn kas-btn-small kas-btn-primary" disabled={!!busy || !operator} onClick={() => setManual(m.key, 'pass')}>Done</button>
                      <button type="button" className="kas-btn kas-btn-small" disabled={!!busy || !operator} onClick={() => setManual(m.key, 'fail')}>Problem</button>
                    </span>
                  </li>
                );
              })}
            </ul>
          </section>

          <section className="kas-card">
            <div className="kas-field-label">5. Sign off</div>
            {run.status === 'passed' ? (
              <div className="kas-verdict kas-pass"><div className="kas-verdict-word">SIGNED OFF</div></div>
            ) : (
              <>
                {!run.signoff.ready && (
                  <ul className="kas-missing">
                    {run.signoff.missing.map((m, i) => <li key={i}>{m.vendorItem ? `PO ${m.poNumber} ${m.vendorItem}: ${m.what}` : m.what}</li>)}
                  </ul>
                )}
                <button type="button" className="kas-btn kas-btn-primary kas-btn-big" disabled={!!busy || !operator || !run.signoff.ready} onClick={signoff}>
                  Sign off this build ({lines.length} lines)
                </button>
              </>
            )}
            {signoffMsg && <div className="kas-hint">{signoffMsg}</div>}
          </section>
        </>
      )}
    </div>
  );
}
